/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useGlobalState } from '@/hooks/useGlobalState';
import useLatestCallback from 'use-latest-callback';
import logger from '@/utils/logger';
import type { AxiosResponse } from 'axios';
import { getAxiosForClient } from '@/lib/axios';

export const useAppData = () => {
  const [appData, setAppData] = useGlobalState('appData');

  const setHeaderFooterStatus = useLatestCallback(
    ({
      headerStatus,
      footerStatus,
      secondaryHeaderFooterStatus,
    }: {
      headerStatus?: boolean;
      footerStatus?: boolean;
      secondaryHeaderFooterStatus?: boolean;
    }) => {
      const updates: Partial<{
        isHeaderPresent: boolean;
        isFooterPresent: boolean;
        isSecondaryHeaderFooterPresent: boolean;
      }> = {};

      if (headerStatus !== undefined) {
        updates.isHeaderPresent = headerStatus;
      }
      if (footerStatus !== undefined) {
        updates.isFooterPresent = footerStatus;
      }
      if (secondaryHeaderFooterStatus !== undefined) {
        updates.isSecondaryHeaderFooterPresent = secondaryHeaderFooterStatus;
      }

      setAppData(updates);
    },
  );

  const setChatAgentStatus = useLatestCallback(
    async (params: { NEW_CHAT_AVAILABLE_ENDPOINT: string; isChatQueueEnabled: boolean }) => {
      const { NEW_CHAT_AVAILABLE_ENDPOINT, isChatQueueEnabled } = params;
      try {
        setAppData({
          isChatAgentAvailable: null,
          chatAgentRequestStatus: 'loading',
        });

        if (isChatQueueEnabled) {
          setAppData({
            isChatAgentAvailable: isChatQueueEnabled,
            chatAgentRequestStatus: 'disabled',
          });
        } else {
          const axios = getAxiosForClient();
          const vendorResponse: AxiosResponse<string> = await axios.get<string>(
            NEW_CHAT_AVAILABLE_ENDPOINT,
            {
              withCredentials: false,
            },
          );

          const regex = /<ns2:count>(\d+)<\/ns2:count>/;
          const match = vendorResponse?.data?.match(regex);
          const chatAgentsAreAvailable = Boolean(match && match.length > 1 && Number(match[1]) > 0);

          setAppData({
            isChatAgentAvailable: chatAgentsAreAvailable,
            chatAgentRequestStatus: 'success',
          });
        }
      } catch (e) {
        logger.warn({
          message: 'Error in chat availability call',
          meta: e,
        });
        setAppData({
          isChatAgentAvailable: false,
          chatAgentRequestStatus: 'error',
        });
      }
    },
  );

  const setFetchingLocationStatus = useLatestCallback((status: boolean) => {
    setAppData({ isFetchingLocation: status });
  });

  const setAuthState = useLatestCallback((sessionAuthState: boolean) => {
    setAppData({ userAuthenticated: sessionAuthState });
  });

  const setAppBannerShown = useLatestCallback((appBannerShown: boolean) => {
    setAppData({ appBannerShown });
  });

  const setCanShowToastNotification = useLatestCallback((canShowToastNotification: boolean) => {
    setAppData({ canShowToastNotification });
  });

  const setIsAutofill = useLatestCallback((isAutofill: boolean) => {
    setAppData({ isAutofill });
  });

  return {
    appData,
    setHeaderFooterStatus,
    setChatAgentStatus,
    setFetchingLocationStatus,
    setAuthState,
    setAppBannerShown,
    setCanShowToastNotification,
    setIsAutofill,
  } as const;
};
